import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

import { useBannerOffset } from "@hooks/useBannerOffset";

import instance from "@store/middleware/api";

import { bannerSurface } from "@utils/bannerGradient";

type Severity = "info" | "warning" | "critical";
type Scope = "global" | "account" | "claims";

interface ActiveBanner {
  id: string;
  message: string;
  severity: Severity;
  dismissKey: string;
  scope: Scope[];
}

/** The full surface per severity — fill plus the texture dial — not just a
    fill, so an incident banner is textured like every other one. */
const SEVERITY_SURFACE: Record<Severity, Record<string, string>> = {
  info: bannerSurface("#4b5563"),
  warning: bannerSurface("#b45309"),
  critical: bannerSurface("rgb(195, 75, 54)"),
};

const DISMISS_STORAGE_KEY = "incident_banner_dismissed";

/** Does the banner's scope target the current page? `global` → everywhere;
    `account` → `/account` + sub-pages; `claims` → `/account/claims` only. */
const matchesScope = (scope: Scope[], path: string): boolean => {
  const s = scope?.length ? scope : (["global"] as Scope[]);
  if (s.includes("global")) return true;
  if (s.includes("account") && (path === "/account" || path.startsWith("/account/"))) return true;
  if (s.includes("claims") && path === "/account/claims") return true;
  return false;
};

/**
 * Site-wide, admin-controlled incident banner (the user-facing half of the
 * incident-comms tool). Polls the PUBLIC `/incident-banner/active` endpoint and
 * renders the active banner where its `scope` targets (global / account pages /
 * claims page). Dismissible per user — the dismissed `dismissKey` is stored in
 * localStorage, so changing the message (which regenerates the key) re-shows it.
 * Dismiss animates out (fade + collapse) like the other global banners. Reserves
 * its rendered height via `--incident-banner-h` (summed in index.css) so page
 * content isn't hidden. Mounted inside SecondHeader + MobileHeader.
 */
const GlobalIncidentBanner: React.FC = () => {
  const { pathname } = useLocation();
  const [banner, setBanner] = useState<ActiveBanner | null>(null);
  const [dismissed, setDismissed] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    instance
      .get("/incident-banner/active")
      .then(({ data }) => {
        const b: ActiveBanner | null = data?.banner ?? null;
        setBanner(b);
        const stored = (() => {
          try {
            return localStorage.getItem(DISMISS_STORAGE_KEY);
          } catch {
            return null;
          }
        })();
        setDismissed(!!b && stored === b.dismissKey);
      })
      .catch(() => setBanner(null));
  }, [pathname]);

  const visible = !!banner && !dismissed && matchesScope(banner.scope, pathname);

  // On /account/* pages (except /account/claims) SecondHeader renders in normal
  // flow (`position: static` — see SecondHeader's `disableFade`), so the banner
  // inside it already reserves its own height. The global `body` offset
  // (--incident-banner-h) is only meant for the FIXED-header pages; adding it
  // here too double-counts and leaves a phantom gap above the header. Mirror the

  // Publish rendered height so page layouts offset their top content.
  useBannerOffset("--incident-banner-h", visible, bannerRef);

  if (!visible || !banner) return null;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, banner.dismissKey);
    } catch {
      /* ignore */
    }
    // Fade + collapse first, then unmount once the transition finishes —
    // same pattern as CancelledGlobalBanner / PastDueGlobalBanner.
    setFadingOut(true);
    window.setTimeout(() => {
      setDismissed(true);
      setFadingOut(false);
    }, 360);
  };

  return (
    <div
      data-banner-surface
      ref={bannerRef}
      data-global-banner
      style={{
        width: "100%",
        marginTop: -2,
        ...(SEVERITY_SURFACE[banner.severity] ?? SEVERITY_SURFACE.warning),
        // Equal on both sides: the dismiss button is positioned above the
        // row rather than in it, so padding that reserves space only on
        // the right would shift the centred text left of the banner's
        // own centre — visibly, on the one banner that had it.
        padding: fadingOut ? "0 44px" : "12px 44px",
        maxHeight: fadingOut ? 0 : 200,
        opacity: fadingOut ? 0 : 1,
        overflow: "hidden",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        color: "#fff",
        fontFamily: '"Raleway", sans-serif',
        fontSize: "0.875rem",
        transition:
          "opacity 360ms cubic-bezier(0.4, 0, 0.2, 1), max-height 360ms cubic-bezier(0.4, 0, 0.2, 1), padding 360ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <span style={{ fontWeight: 500, whiteSpace: "pre-wrap", textAlign: "center" }}>{banner.message}</span>
      <button
        aria-label="Dismiss"
        className="banner-dismiss"
        onClick={dismiss}
        style={{
          position: "absolute",
          right: 14,
          top: "50%",
          transform: "translateY(-50%)",
          background: "transparent",
          border: "none",
          color: "rgba(255,255,255,0.98)",
          cursor: "pointer",
          padding: 4,
          lineHeight: 0,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.color = "#fff")}
        onMouseLeave={(e) => (e.currentTarget.style.color = "rgba(255,255,255,0.98)")}
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    </div>
  );
};

export default GlobalIncidentBanner;
